import React, { useState, useRef, useEffect } from 'react';

export const COLORS = [
  '#2563eb',
  '#16a34a',
  '#f59e0b',
  '#dc2626',
  '#7c3aed',
  '#0891b2',
  '#db2777',
  '#64748b',
];

export const STAGE_COLORS = {
  New: '#94a3b8',
  Contacted: '#60a5fa',
  Qualified: '#2563eb',
  Proposal: '#7c3aed',
  Negotiation: '#f59e0b',
  Won: '#16a34a',
  Lost: '#dc2626',
};

export const formatNumber = (n, digits = 0) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString(undefined, { maximumFractionDigits: digits });

export const formatPercent = (n) =>
  n === null || n === undefined ? '—' : `${formatNumber(n, 1)}%`;

/** {MYR: 1000, USD: 50} -> "MYR 1,000 · USD 50" (never sums across currencies). */
export const formatMoneyMap = (map) => {
  const entries = Object.entries(map || {});
  if (!entries.length) return '—';
  return entries
    .map(([cur, v]) => {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: cur,
          maximumFractionDigits: 0,
        }).format(v);
      } catch {
        return `${cur} ${formatNumber(v)}`;
      }
    })
    .join(' · ');
};

export const moneyIn = (map, currency) => Number((map || {})[currency] || 0);

/** Formats one amount in one currency, e.g. formatCurrency(1200, 'USD') -> "$1,200". */
export const formatCurrency = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${formatNumber(amount)}`;
  }
};

/**
 * Converts a USD total into another currency using "1 USD = X" rates from the
 * server. Returns null when no rate is available (never guesses).
 */
export const convertFromUsd = (usd, target, rates) => {
  if (usd === null || usd === undefined) return null;
  if (target === 'USD') return usd;
  const rate = Number(rates?.[target]);
  return rate && Number.isFinite(rate) ? usd * rate : null;
};

/** Currency picker with Image 3 trigger design and custom floating dropdown menu */
export const CurrencySelect = ({ value, onChange, rates, id = 'analytics-currency' }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const options = Object.keys(rates || { USD: 1 }).sort((a, b) =>
    a === 'USD' ? -1 : b === 'USD' ? 1 : a.localeCompare(b)
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div
      ref={dropdownRef}
      className="analytics-currency"
      title="Totals are calculated in USD, then converted"
    >
      <button
        type="button"
        className={`analytics-currency-trigger ${open ? 'active' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="analytics-currency-left">
          <i className="fas fa-coins currency-coin-icon" aria-hidden="true"></i>
          <span>Currency</span>
        </span>
        <span className="analytics-currency-divider"></span>
        <span className="analytics-currency-right">
          <span className="currency-current-val">{value}</span>
          <i
            className={`fa-solid fa-chevron-down currency-chevron ${open ? 'open' : ''}`}
            aria-hidden="true"
          ></i>
        </span>
      </button>

      {/* Hidden select element for accessibility and test suite assertions */}
      <select
        id={id}
        className="analytics-sr-only"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        tabIndex={-1}
        aria-hidden="true"
      >
        {options.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>

      {open && (
        <div className="analytics-currency-menu" role="listbox" aria-label="Select currency">
          {options.map((c) => (
            <button
              key={c}
              type="button"
              role="option"
              aria-selected={value === c}
              className={`currency-menu-item ${value === c ? 'active' : ''}`}
              onClick={() => {
                onChange(c);
                setOpen(false);
              }}
            >
              <span className="currency-code">{c}</span>
              {value === c && (
                <i className="fa-solid fa-check currency-check" aria-hidden="true"></i>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** "2026-10-05" / "2026-10" -> "5 Oct" / "Oct 2026" */
export const formatPeriodLabel = (key, granularity) => {
  if (!key) return '';
  if (granularity === 'month') {
    const d = new Date(`${key}-01T00:00:00Z`);
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
  }
  const d = new Date(`${key}T00:00:00Z`);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
};

export const KpiCard = ({ label, value, hint, icon, tone = 'blue' }) => (
  <div className={`analytics-kpi tone-${tone}`}>
    <div className="analytics-kpi-head">
      {icon && <i className={`fas ${icon}`} aria-hidden="true"></i>}
      <span className="analytics-kpi-label">{label}</span>
    </div>
    <div className="analytics-kpi-value">{value}</div>
    {hint && <div className="analytics-kpi-hint">{hint}</div>}
  </div>
);

/**
 * Card wrapper for a chart. `table` ({columns, rows}) is rendered as a
 * screen-reader-only table so chart insights are accessible without visuals.
 */
export const ChartCard = ({
  title,
  subtitle,
  children,
  table,
  empty,
  emptyText,
  wide,
  actions,
}) => (
  <section className={`analytics-card ${wide ? 'wide' : ''}`} aria-label={title}>
    <header className="analytics-card-head">
      <div>
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions}
    </header>
    {empty ? (
      <div className="analytics-chart-empty">{emptyText || 'No data for this period yet.'}</div>
    ) : (
      <>
        <div className="analytics-chart" aria-hidden={table ? 'true' : undefined}>
          {children}
        </div>
        {table && <SrTable caption={title} {...table} />}
      </>
    )}
  </section>
);

export const SrTable = ({ caption, columns, rows }) => (
  <table className="analytics-sr-only">
    <caption>{caption}</caption>
    <thead>
      <tr>
        {columns.map((c) => (
          <th key={c} scope="col">
            {c}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((r, i) => (
        <tr key={i}>
          {r.map((cell, j) => (
            <td key={j}>{cell}</td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

export const DataTable = ({ columns, rows, caption }) => (
  <div className="analytics-table-wrap">
    <table className="analytics-table">
      {caption && <caption className="analytics-sr-only">{caption}</caption>}
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key || c.label} scope="col" className={c.align === 'right' ? 'num' : ''}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.id || i}>
            {columns.map((c) => (
              <td key={c.key || c.label} className={c.align === 'right' ? 'num' : ''}>
                {c.render ? c.render(r) : r[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Notes = ({ items }) =>
  items && items.length ? (
    <ul className="analytics-notes">
      {items.map((n) => (
        <li key={n}>
          <i className="fas fa-circle-info" aria-hidden="true"></i> {n}
        </li>
      ))}
    </ul>
  ) : null;

export const NotTrackedChips = ({ items, label = 'Not tracked yet' }) =>
  items && items.length ? (
    <div className="analytics-not-tracked">
      <span>{label}:</span>
      {items.map((i) => (
        <span key={i} className="analytics-chip">
          {i}
        </span>
      ))}
    </div>
  ) : null;

const TAB_ICONS = {
  content: 'fa-file-lines',
  experiments: 'fa-flask',
};

export const Unavailable = ({ reason, tab }) => (
  <div className="analytics-unavailable">
    <div className="analytics-unavailable-icon" aria-hidden="true">
      <i className={`fas ${TAB_ICONS[tab] || 'fa-lock'}`}></i>
    </div>
    <h2>Nothing to show here yet</h2>
    <p>{reason || 'This section is not available right now.'}</p>
  </div>
);

/** Shared tooltip styling for recharts. */
export const tooltipProps = {
  contentStyle: {
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    fontSize: 12,
    boxShadow: '0 4px 12px rgba(15,23,42,0.08)',
  },
  labelStyle: { fontWeight: 600, color: '#0f172a' },
};
